import React from 'react';

import { Switch } from '@mui/material';

import { DARK_GREY, SCOUT_TEAL, WHITE } from '../../ThemeContext/constants';

import type { SwitchProps } from '@mui/material';
import type { ChangeEvent } from 'react';

export interface ToggleProps {
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  size?: SwitchProps['size'];
  disabled?: boolean;
  label?: string;
  supportingText?: string;
  id?: string;
}

const getLabelColor = (disabled: boolean): string => (disabled ? DARK_GREY : '#333333');

const switchSx = {
  '& .MuiSwitch-track': {
    backgroundColor: '#D0D0D0',
    opacity: 1
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: WHITE
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    '& .MuiSwitch-thumb': {
      backgroundColor: SCOUT_TEAL
    },
    '& + .MuiSwitch-track': {
      backgroundColor: '#CCEDEB',
      opacity: 1
    },
    '&:hover .MuiSwitch-thumb': {
      backgroundColor: '#53B2AE'
    }
  },
  '& .MuiSwitch-switchBase.Mui-disabled': {
    '& .MuiSwitch-thumb': {
      backgroundColor: '#F5F5F5'
    },
    '& + .MuiSwitch-track': {
      opacity: 0.5
    }
  },
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
    '& .MuiSwitch-thumb': {
      backgroundColor: '#CCEDEB'
    }
  }
};

const Toggle = ({
  checked,
  onChange,
  size = 'medium',
  disabled = false,
  label,
  supportingText,
  id
}: ToggleProps): React.ReactElement => {
  const switchElement = (
    <Switch
      checked={checked}
      disabled={disabled}
      id={id}
      onChange={onChange}
      size={size}
      sx={switchSx}
    />
  );

  if (!label) {
    return switchElement;
  }

  return (
    <label
      htmlFor={id}
      style={{
        display: 'inline-flex',
        alignItems: supportingText ? 'flex-start' : 'center',
        columnGap: '8px',
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      {supportingText ? (
        <span style={{ marginTop: size === 'small' ? '-7px' : '-12px' }}>{switchElement}</span>
      ) : (
        switchElement
      )}
      <span style={{ display: 'flex', flexDirection: 'column', rowGap: '4px' }}>
        <span
          style={{
            fontFamily: 'Source Sans Pro, sans-serif',
            fontSize: size === 'small' ? '14px' : '16px',
            lineHeight: size === 'small' ? '18px' : '20px',
            color: getLabelColor(disabled)
          }}
        >
          {label}
        </span>
        {supportingText ? (
          <span
            style={{
              fontFamily: 'Source Sans Pro, sans-serif',
              fontSize: '14px',
              lineHeight: '18px',
              color: disabled ? '#989898' : DARK_GREY
            }}
          >
            {supportingText}
          </span>
        ) : null}
      </span>
    </label>
  );
};

export default Toggle;
